import React, { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  Users,
  Settings,
  BarChart3,
  FileText,
  UserCog,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Bell,
  CreditCard,
  Shield,
  Activity,
  Zap,
  HelpCircle,
  Archive,
  Monitor,
  Key,
  Store,
  LogOut,
  Tag,
  Sliders,
  Share2,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/utils';
import { useCurrentUser, useAppDispatch } from '@/hooks/redux';
import { useLogout } from '@/hooks/useLogout';
import { logout as logoutAction } from '@/store/slices/authSlice';
import { UserRole } from '@/types';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

interface NavItem {
  name: string;
  href?: string;
  icon: React.ComponentType<{ className?: string }>;
  adminOnly?: boolean;
  badge?: string;
  children?: NavItem[];
}

const navigation: NavItem[] = [
  { name: 'Dashboard', href: '/dashboard', icon: Home },
  { name: 'All Users', href: '/users', icon: Users, adminOnly: true },
  {
    name: 'Merchant Management',
    icon: Store,
    adminOnly: true,
    children: [
      { name: 'All Merchant', href: '/vendors', icon: Store },
      { name: 'Merchant Requests', href: '/partnership-requests', icon: FileText },
      { name: 'Merchant Sales', href: '/vendor-sales', icon: BarChart3 },
      { name: 'Categories', href: '/categories', icon: Tag },
    ],
  },
  { name: 'Portal Config', href: '/portal-config', icon: Sliders, adminOnly: true },
  { name: 'Referral Config', href: '/referral-config', icon: Share2, adminOnly: true },
  { name: 'Referral Report', href: '/referral-report', icon: FileText, adminOnly: true },
  { name: 'Profile', href: '/profile', icon: UserCog, adminOnly: true },
];

interface NavItemComponentProps {
  item: NavItem;
  isOpen: boolean;
  level?: number;
}

function NavItemComponent({ item, isOpen, level = 0 }: NavItemComponentProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const location = useLocation();
  const user = useCurrentUser();
  const isAdmin = user?.role === UserRole.ADMIN;

  // Don't render admin-only items for non-admin users
  if (item.adminOnly && !isAdmin) {
    return null;
  }

  const hasChildren = item.children && item.children.length > 0;
  const isActive = item.href ? location.pathname === item.href : false;
  const hasActiveChild = item.children?.some(
    (child) => child.href && location.pathname.startsWith(child.href)
  );

  // Auto-expand if has active child
  React.useEffect(() => {
    if (hasActiveChild && isOpen) {
      setIsExpanded(true);
    }
  }, [hasActiveChild, isOpen]);

  const handleClick = () => {
    if (hasChildren) {
      setIsExpanded(!isExpanded);
    }
  };

  const Icon = item.icon;
  const indent = level * 16;

  if (hasChildren) {
    return (
      <div>
        <button
          onClick={handleClick}
          className={cn(
            'flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors',
            isActive || hasActiveChild
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
            !isOpen && 'justify-center'
          )}
          style={{ paddingLeft: isOpen ? `${12 + indent}px` : undefined }}
          title={!isOpen ? item.name : undefined}
        >
          <Icon className={cn('h-5 w-5', isOpen && 'mr-3')} />
          {isOpen && (
            <>
              <span className="flex-1 text-left">{item.name}</span>
              {item.badge && (
                <span className="ml-2 inline-flex items-center rounded-full bg-primary/20 px-2 py-0.5 text-xs font-medium">
                  {item.badge}
                </span>
              )}
              {isExpanded ? (
                <ChevronUp className="ml-2 h-4 w-4" />
              ) : (
                <ChevronDown className="ml-2 h-4 w-4" />
              )}
            </>
          )}
        </button>

        {/* Submenu */}
        {hasChildren && isExpanded && isOpen && (
          <div className="mt-1 space-y-1">
            {item.children!.map((child) => (
              <NavItemComponent key={child.name} item={child} isOpen={isOpen} level={level + 1} />
            ))}
          </div>
        )}
      </div>
    );
  }

  // Regular nav item
  return (
    <NavLink
      to={item.href!}
      className={({ isActive }) =>
        cn(
          'flex items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors',
          isActive
            ? 'bg-primary text-primary-foreground'
            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
          !isOpen && 'justify-center'
        )
      }
      style={{ paddingLeft: isOpen ? `${12 + indent}px` : undefined }}
      title={!isOpen ? item.name : undefined}
    >
      <Icon className={cn('h-5 w-5', isOpen && 'mr-3')} />
      {isOpen && (
        <>
          <span>{item.name}</span>
          {item.badge && (
            <span className="ml-auto inline-flex items-center rounded-full bg-primary/20 px-2 py-0.5 text-xs font-medium">
              {item.badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

export function EnhancedSidebar({ isOpen, onToggle }: SidebarProps) {
  const user = useCurrentUser();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { mutate: logoutMutate } = useLogout();

  const handleLogout = () => {
    logoutMutate(undefined, {
      onSettled: () => {
        dispatch(logoutAction());
        navigate('/login', { replace: true });
      },
    });
  };

  return (
    <>
      {/* Sidebar backdrop for mobile */}
      {isOpen && <div className="fixed inset-0 z-20 bg-black/50 lg:hidden" onClick={onToggle} />}

      {/* Sidebar */}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-30 flex flex-col bg-card border-r transition-all duration-300 overflow-hidden',
          isOpen ? 'w-64 translate-x-0' : 'w-16 -translate-x-full lg:translate-x-0'
        )}
      >
        {/* Sidebar header */}
        <div
          className={cn(
            'flex h-16 items-center justify-between border-b border-border',
            isOpen ? 'px-4' : 'px-1.5'
          )}
        >
          <div className="flex items-center min-w-0 flex-1">
            <svg
              className={cn(
                'shrink-0 drop-shadow-[0_0_4px_rgba(243,34,125,0.4)] animate-pulse',
                isOpen ? 'h-8 w-8' : 'h-7 w-7'
              )}
              viewBox="0 0 48 48"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <defs>
                <linearGradient id="mintGradLogoSidebar" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#F3227D"></stop>
                  <stop offset="100%" stopColor="#FFB600"></stop>
                </linearGradient>
              </defs>
              <path
                d="M24 4C14 16 10 24 10 32C10 40 16 44 24 44C32 44 38 40 38 32C38 24 34 16 24 4Z"
                fill="url(#mintGradLogoSidebar)"
                stroke="#000000"
                strokeWidth="3"
              ></path>
              <circle
                cx="24"
                cy="30"
                r="8"
                fill="#121212"
                stroke="#000000"
                strokeWidth="3"
              ></circle>
              <circle cx="24" cy="30" r="4" fill="#FFB600"></circle>
            </svg>
            {isOpen && (
              <div className="ml-3 flex-1 min-w-0">
                <div className="text-sm font-black tracking-tight text-foreground uppercase truncate">
                  MST<span className="text-[#6F239B] ml-0.5">Mint</span>
                </div>
                <div className="text-[9px] uppercase font-bold tracking-widest text-[#FFB600] truncate">
                  Admin Panel
                </div>
              </div>
            )}
          </div>

          {/* Toggle button - desktop only */}
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggle}
            className="hidden lg:flex h-6 w-6 shrink-0 text-muted-foreground hover:text-foreground p-0 ml-1"
            title={isOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            {isOpen ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-1 p-2 overflow-y-auto">
          {navigation.map((item) => (
            <NavItemComponent key={item.name} item={item} isOpen={isOpen} />
          ))}
        </nav>

        {/* User info */}
        {/* {isOpen && user && (
          <div className="border-t p-4">
            <div className="flex items-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground">
                {user.name.charAt(0).toUpperCase()}
              </div>
              <div className="ml-3 min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{user.name}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {user.role.toLowerCase()} • {user.email}
                </p>
              </div>
            </div>

            <div className="mt-3 flex space-x-2">
              <Button variant="outline" size="sm" className="flex-1">
                <UserCog className="h-3 w-3 mr-1" />
                Profile
              </Button>
              <Button variant="outline" size="sm" className="flex-1">
                <Settings className="h-3 w-3 mr-1" />
                Settings
              </Button>
            </div>
          </div>
        )} */}

        {/* Collapsed user indicator */}
        {/* {!isOpen && user && (
          <div className="border-t p-2">
            <div className="flex justify-center">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm">
                {user.name.charAt(0).toUpperCase()}
              </div>
            </div>
          </div>
        )} */}

        {/* Logout - always visible regardless of whether profile info has loaded */}
        <div className={cn('border-t p-2', isOpen && 'p-4 pt-2')}>
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              'text-muted-foreground hover:text-destructive',
              isOpen ? 'w-full justify-center' : 'w-8 h-8 mx-auto flex p-0'
            )}
            onClick={handleLogout}
            title={!isOpen ? 'Log out' : undefined}
          >
            <LogOut className={cn('h-4 w-4', isOpen && 'mr-1')} />
            {isOpen && 'Log out'}
          </Button>
        </div>
      </div>
    </>
  );
}
